<template>
  <div>
    <div class="columns is-multiline is-vcentered has-less-padding">
      <div class="column is-4">
        <textbox
          v-model="searchString"
          placeholder="Search by name"
          name="search"
          type="text"
          class="cell no-margin"
        />
      </div>
      <div class="column is-4">
        <v-selectmenu
          v-if="agencies"
          v-model="selectedAgencies"
          key-field="id"
          show-field="description"
          :data="agencies"
          :language="'en'"
          :multiple="true"
          :full-width="true"
          :query="true"
          :title="'Select Roles'"
        >
          <button
            id="selected-departments"
            type="button"
            class="sm-default-btn"
          >
            <div>
              <strong>Departments</strong>{{ selectedAgencies !== '' ? ': ' + getDropdownLabel(selectedAgencies, 'agencies') : '' }}
            </div>
          </button>
        </v-selectmenu>
      </div>
      <div class="column is-4">
        <dropdown
          v-model="selectedYear"
          :options="years"
          value-key="description"
          text-key="description"
          default-option="Year"
          placeholder="Year"
        />
      </div>
      <div class="column is-4">
        <v-selectmenu
          v-model="selectedStatements"
          key-field="id"
          show-field="description"
          :data="requiredStatements"
          :language="'en'"
          :multiple="true"
          :full-width="true"
          :query="false"
          :title="'Select Statements'"
        >
          <button
            id="selected-statements"
            type="button"
            class="sm-default-btn"
          >
            <div>
              <strong>Statements</strong> {{ selectedStatements }}
            </div>
          </button>
        </v-selectmenu>
      </div>
      <div class="column is-3">
        <checkbox-adv
          v-model="includeInactiveUsers"
          :value="true"
          class="no-margin"
        >
          Include inactive filers
        </checkbox-adv>
      </div>
      <div class="column is-3 has-text-right">
        <button
          id="search-btn"
          class="button is-primary"
          type="submit"
          @click="filterUsers"
        >
          <span>Search</span>
          <span class="icon is-small">
            <i class="fa fa-search" />
          </span>
        </button>
      </div>
      <div class="column is-2 has-text-right">
        <button
          id="search-btn"
          class="button is-secondary"
          :class="{ 'is-loading' : isDownloading }"
          type="submit"
          :disabled="isDownloading"
          @click.prevent="exportCsv"
        >
          <span>Download CSV</span>
          <span class="icon is-small">
            <i class="fad fa-file-download" />
          </span>
        </button>
      </div>
    </div>
    <v-server-table
      ref="filersStatusResults"
      :columns="tableColumns"
      :options="tableOptions"
    >
      <template
        slot="userName"
        slot-scope="props"
      >
        <div class="legend-icons">
          <span
            v-if="props.row.active !== 'Y'"
            class="icon has-text-danger is-small"
          >
            <i class="fad fa-xs fa-user-circle" />
          </span>
          <span
            v-if="props.row.sysAdmin === 'Y'"
            class="icon has-text-electric-blue is-small"
          >
            <i class="fad fa-xs fa-user-circle" />
          </span>
          <span
            v-if="props.row.deptHead === 'Y'"
            class="icon has-text-phanatic-green is-small"
          >
            <i class="fad fa-xs fa-user-circle" />
          </span>
        </div>
        {{ props.row.userName }}
      </template>
      <template
        slot="primaryPosition"
        slot-scope="props"
      >
        <template v-if="props.row.primaryAgencyId">
          {{ getControlValueDesc(props.row.primaryAgencyId, 'agencies') }}
        </template>
      </template>
      <template
        slot="requiredStatements"
        slot-scope="props"
      >
        <div class="columns has-text-centered">
          <div
            :class="{ disabled: props.row.stateForm !== 'Y' }"
            class="column"
          >
            <i
              v-show="props.row.stateForm === 'Y'"
              class="far fa-check-square"
            />
            <i
              v-show="props.row.stateForm !== 'Y'"
              class="far fa-square"
            />
          </div>
          <div
            :class="{ disabled: props.row.cityForm !== 'Y' }"
            class="column"
          >
            <i
              v-show="props.row.cityForm === 'Y'"
              class="far fa-check-square"
            />
            <i
              v-show="props.row.cityForm !== 'Y'"
              class="far fa-square"
            />
          </div>
          <div
            :class="{ disabled: props.row.mayorForm !== 'Y' }"
            class="column"
          >
            <i
              v-show="props.row.mayorForm === 'Y'"
              class="far fa-check-square"
            />
            <i
              v-show="props.row.mayorForm !== 'Y'"
              class="far fa-square"
            />
          </div>
        </div>
      </template>

      <template
        slot="editFiler"
        slot-scope="props"
      >
        <a
          :title="`Edit ${props.row.firstName} ${props.row.lastName} profile`"
          href="javascript:;"
          @click.prevent="editFiler(props.row)"
        >
          <i class="fas fa-user-edit" />
        </a>
      </template>
      <template
        slot="cityForm"
        slot-scope="props"
      >
        {{ getFilingStatus(props.row.cityForm, props.row.isCityReceived) }}
      </template>
      <template
        slot="stateForm"
        slot-scope="props"
      >
        {{ getFilingStatus(props.row.stateForm, props.row.isStateReceived) }}
      </template>
      <template
        slot="mayorForm"
        slot-scope="props"
      >
        {{ getFilingStatus(props.row.mayorForm, props.row.isMayorReceived) }}
      </template>
    </v-server-table>
  </div>
</template>
<script>
import Vue from 'vue';
import { ServerTable } from 'vue-tables-2';
import { Textbox, Dropdown } from '@phila/phila-ui';
import vSelectMenu from 'v-selectmenu';
import { controlValueHelpers } from '@/mixins/controlValue';
import { ExportToCsv } from 'export-to-csv';
import CheckboxAdv from '@/components/phila-ui/CheckboxAdv';
Vue.use(vSelectMenu);
Vue.use(ServerTable, {}, false, 'bulma');
export default {
  name: 'FilersStatus',
  components: {
    Dropdown,
    Textbox,
    CheckboxAdv,
  },
  mixins: [
    controlValueHelpers,
  ],
  data () {
    return {
      agencies: [],
      deptAccess: [],
      userProfile: {},
      isDownloading: false,
      currentQuery: {},
      currentCount: 0,
      searchString: '',
      selectedAgencies: '',
      selectedYear: '',
      selectedStatements: '',
      includeInactiveUsers: false,
      requiredStatements: [
        {
          id: 'city',
          description: 'City',
        },
        {
          id: 'state',
          description: 'State',
        },
        {
          id: 'mayor',
          description: 'Mayor',
        },
      ],
      tableColumns: [
        "filerName",
        "departmentName",
        "cityForm",
        "stateForm",
        "mayorForm",
        "year",
      ],
      tableOptions: {
        requestFunction: this.customRequestFunction,
        responseAdapter: this.customResponseAdapter,
        headings: {
          filerName: 'Full Name',
          departmentName: 'Department',
          cityForm: 'City',
          stateForm: 'State',
          mayorForm: 'Mayor',
          requiredStatements: function() {
            return <div>
                Required Statements
              <div class="columns">
                <div class="column">
                    State
                </div>
                <div class="column">
                    City
                </div>
                <div class="column">
                    Mayor
                </div>
              </div>
            </div>;
          },
          editFiler: "Edit",
        },
        columnsClasses: {
          id: 'text-center',
          requiredStatements: "has-text-centered",
          editFiler: "has-text-centered",
          userStatements: "has-text-centered",
        },
        sortIcon: {
          is: "fal fa-sort",
          up: "fad fa-sort-up",
          down: "fad fa-sort-down",
        },
        perPage: 20,
        perPageValues: [],
        sortable: [ "filerName", "departmentName" ],
        filterable: false,
        pagination: {
          edge: true,
          nav: 'scroll',
        },
      },
    };
  },
  computed: {
    years () {
      let years = this.$store.getters['admin/activeControlValues']('filingyears');
      if (years) {
        return this.cloneObj(this.$store.getters['admin/activeControlValues']('filingyears')).sort((a, b) => {
          return b.sequenceNo - a.sequenceNo;
        });
      }
      return [];
    },
  },
  async created () {
    this.$store.commit('loading', true);
    await this.initialFetch();
    await this.initialAssign();
    this.$store.commit('loading', false);
  },
  methods: {
    async exportCsv () {
      this.isDownloading = true;
      if (Object.keys(this.currentQuery).length > 0) {
        this.currentQuery.pageSize = this.currentCount;
        let data = await this.$store.dispatch('reports/fetchFilersStatus', this.currentQuery);
        let exportData = [];
        data.filings.forEach(filing => {
          exportData.push({
            filerName: filing.filerName,
            departmentName: filing.departmentName,
            cityForm: this.getFilingStatus(filing.cityForm, filing.isCityReceived),
            stateForm: this.getFilingStatus(filing.stateForm, filing.isStateReceived),
            mayorForm: this.getFilingStatus(filing.mayorForm, filing.isMayorReceived),
            year: filing.year,
          });
        });
        const csvExporter = new ExportToCsv({
          filename: 'user-status-report',
          useKeysAsHeaders: false,
          showLabels: true,
          showTitle: false,
          headers: [ 'Full Name', 'Department', 'City', 'State', 'Mayor', 'Year' ],
        });
        csvExporter.generateCsv(exportData);
        this.isDownloading = false;
      }
    },
    async initialAssign () {
      this.selectedYear = this.years[0].description;
      this.userProfile = await this.getUserProfile();
      this.deptAccess = await this.getUserDeptsAccess();
      this.agencies = await this.getAgencies();
    },
    async initialFetch () {
      let promises = [];
      promises.push(this.$store.dispatch('admin/getControlValue', 'filingyears'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'agency'));
      // promises.push(this.$store.dispatch('user/fetchUserProfile'));
      promises.push(this.$store.dispatch('user/fetchUserDepts'));
      return Promise.all(promises);
    },
    getUserProfile () {
      return this.cloneObj(this.$store.state.user.profile);
    },
    getAgencies () {
      if (this.userProfile.sysAdmin === 'Y') {
        return this.cloneObj(this.$store.getters[ 'admin/activeControlValues' ]('agency'));
      }
      let agencies = this.cloneObj(this.$store.getters[ 'admin/activeControlValues' ]('agency'));
      let allowedAgencies = agencies.filter(agency => {
        if (this.deptAccess.includes(agency.id)){
          return true;
        }
        return false;
      });
      return allowedAgencies;
    },
    getDropdownLabel (keys, variable) {
      const keysArray = keys.split(",");
      let labels = [];
      keysArray.forEach(key => {
        this[variable].forEach(item => {
          if (item.id == key) {
            labels.push(item.description);
          }
        });
      });
      return labels.join(', ');
    },
    async customRequestFunction (params) {
      const query = {
        ascending: params.ascending === 1 ? true : false,
        years: [ this.selectedYear ],
        searchString: this.searchString,
        pageNumber: params.page,
        pageSize: params.limit,
        includeInactiveUsers: this.includeInactiveUsers,
        orderByCol: params.orderBy ? params.orderBy.charAt(0).toUpperCase() + params.orderBy.slice(1) : params.orderBy,
      };
      if (this.selectedAgencies !== '') {
        query.departmentIds = this.selectedAgencies.split(',').map(Number);
      }
      if (this.selectedStatements !== '') {
        query.statementTypes = this.selectedStatements.split(',');
      }
      this.currentQuery = query;
      try {
        return await this.$store.dispatch('reports/fetchFilersStatus', query);
      } catch (err) {
        return [];
      }
    },
    customResponseAdapter (data) {
      const resp = { data: [], count: 0 };
      if (data) {
        resp.data = data.filings;
        resp.count = data.totalCount;
        this.currentCount = data.totalCount;
      }
      return resp;
    },
    getFilingStatus (requirement, status) {
      if (requirement === 'Y' && requirement === status) {
        return 'completed';
      }
      if (requirement === 'Y' && requirement !== status) {
        return 'not completed';
      }
      if (requirement !== 'Y') {
        return 'not required';
      }
    },
    filterUsers () {
      this.$refs['filersStatusResults'].refresh();
    },
    getUserDeptsAccess () {
      return this.$store.state.user.deptAccess;
    },
  },
};
</script>

<style lang="scss">
  .columns.has-less-padding {
    .column {
      padding: .4rem 0.75em ;
    }
  }
  .v-dropdown-caller {
    width: 100%;
    .sm-caller-container {
      width: 100%;
      button.sm-default-btn {
        width: 100%;
        max-width: 100%;
        text-align: left;
        border-radius: 0;
        box-shadow: none;
        height: 40px;
        padding: 0 0 0 10px;
        vertical-align: middle;
        > div {
          max-width: 90%;
          display: inline-block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          line-height: 40px;
          &:before {
            content: " ";
            background: white;
            width: 30px;
            height: 100%;
            display: block;
            z-index: 9;
            top: 0;
            right: 0;
            position: absolute;
          }
          &:after {
            content: "";
            right: 1.125em;
            z-index: 10;
            border: 3px solid transparent;
            border-color: #0f4d8f;
            border-radius: 2px;
            border-right: 0;
            border-top: 0;
            display: block;
            height: 0.625em;
            margin-top: -0.4375em;
            pointer-events: none;
            position: absolute;
            top: 50%;
            transform-origin: center;
            width: 0.625em;
            transform: rotate(-45deg);
          }
        }
      }
    }
  }
</style>